import React from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { cn } from '@/lib/utils.ts'

const carVariants = cva('', {
  variants: {
    state: {
      empty: '',
      available: 'fill-white stroke-gray-800',
      assigned: 'fill-white stroke-blue-600 stroke-2',
      allocated: 'fill-blue-600 stroke-blue-900',
      onboarded: 'fill-green-500 stroke-green-900',
      unfindabled: 'fill-orange-400 stroke-orange-800',
      pending: 'fill-gray-100 stroke-gray-500',
      onHold: 'stroke-orange-600',
      inReturn: 'stroke-orange-600',
      onboardFlagged: '',
      completedSuccess: 'fill-green-500 stroke-green-900',
      completedFailure: 'fill-red-500 stroke-red-800',
    },
  },
  defaultVariants: {
    state: 'pending',
  },
})

const carNumberVariants = cva('z-10 pt-2', {
  variants: {
    state: {
      empty: '',
      available: 'text-black',
      assigned: 'text-blue-600',
      allocated: 'text-white',
      onboarded: 'text-white',
      unfindabled: 'text-orange-800',
      pending: 'text-gray-400',
      onHold: 'text-orange-600',
      inReturn: 'text-orange-600',
      onboardFlagged: 'text-green-500',
      completedSuccess: 'text-white',
      completedFailure: 'text-white',
    },
  },
  defaultVariants: {
    state: 'pending',
  },
})

export interface CarProps extends React.HtmlHTMLAttributes<HTMLDivElement>, VariantProps<typeof carVariants> {
  carNumber: string
  carType?: 'automatic' | 'manual' | 'electric'
  onMap?: boolean
  vip?: boolean
}

export const Car = React.forwardRef<HTMLDivElement, CarProps>(
  ({ className, state, vip, carNumber, carType = 'automatic', onMap, ...props }, ref) => {
    let additionalProps: Record<string, string> =
      state === 'pending' || state === 'onboardFlagged'
        ? { strokeLinecap: 'square', strokeLinejoin: 'bevel', strokeDasharray: '2 2' }
        : {}
    if (state === 'onboardFlagged') {
      additionalProps = { ...additionalProps, stroke: '#5A8B84', fill: '#B9E6D6' }
    }

    return (
      <div
        className={cn(
          'relative flex h-[37px] w-[38px] justify-center bg-transparent text-base leading-[100%] font-bold tracking-[-0.42px] select-none',
          className
        )}
        ref={ref}
        {...props}>
        <p className={cn(carNumberVariants({ state }))}>{carNumber}</p>

        {carType === 'manual' && <Icons.carTypeManual className="absolute -top-px right-px z-10" />}

        {carType === 'electric' && <Icons.carTypeElectric className="absolute -top-px right-px z-10" />}

        {state === 'onHold' && <Icons.clock className="absolute -bottom-px -left-px z-10 size-4 text-orange-600" />}

        {state === 'inReturn' && (
          <div className="absolute bottom-1 z-10 flex w-4 items-center justify-center rounded-[2px] bg-black px-0.5 py-px">
            <p className="text-center text-[9px] leading-[100%] font-semibold text-white">R</p>
          </div>
        )}

        {state === 'completedSuccess' && (
          <>
            <Icons.circle className="absolute -bottom-px -left-px z-10 size-4 fill-green-500 stroke-green-900" />
            <Icons.check className="absolute bottom-[3px] left-[3px] z-10 size-2 stroke-white" />
          </>
        )}

        {state === 'completedFailure' && (
          <>
            <Icons.circle className="absolute -bottom-px -left-px z-10 size-4 fill-red-500 stroke-red-800" />
            <Icons.x className="absolute bottom-[4px] left-[4px] z-10 size-1.5 fill-white stroke-red-800" />
          </>
        )}

        <div className="absolute top-0 left-0 h-[37px] w-[38px]">
          <Icons.car
            className={cn(carVariants({ state }))}
            vip={vip ?? false}
            shadow={onMap ?? false}
            flagged={state === 'onboardFlagged'}
            {...additionalProps}
          />
        </div>
      </div>
    )
  }
)
